/**
 * HubLayout — the persistent content tree for the Hub space, plus the outlet for whichever
 * category overview or document is currently open. Mirrors the top-level <Layout> pattern in
 * App.tsx (a chrome component wrapping <Outlet/>), one level deeper: <Layout> provides the
 * app-wide navbar/sidebar, this provides Hub's own library sidebar inside that.
 *
 * Two navigation layers exist on purpose, matching the space-switcher-plus-content-tree shape
 * real document tools use (Confluence/GitBook): the app sidebar's four Hub links are quick jumps
 * between categories; this tree is the "what's actually in here" map, nested down to individual
 * documents, that GitBook/Confluence keep separate from top-level space navigation.
 *
 * Subordinate policies (a document grouped under another) are a real parentDocumentId link, set
 * by dragging one document's row onto another's - not derived from domainArea/outlineLevel, an
 * earlier mechanism that only ever worked if that free-text "domain area" field happened to
 * contain a bare digit, which the Properties tab that actually writes it never produces, so it
 * silently never grouped anything for real content. Dropping onto a category's own header
 * clears parentDocumentId, promoting a document back to top-level.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { documentApi, parseApiError } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { HUB_CATEGORIES, type HubCategoryDef, type DocTreeNode, buildDocTree, flattenDocTree, effectiveStatus } from './hubUtils';

interface HubDoc {
  documentId: string;
  title?: string;
  domainArea?: string;
  documentType?: string;
  status?: string;
  nextReviewDate?: string;
  uploadedAt?: string;
  parentDocumentId?: string;
}

/** DPIAs are entity-linked, not register-linked, so the DPIAs category can't resolve a
 * registerId the way every other category does - it fetches every tenant document instead and
 * filters by documentType client-side, the same technique DocumentsList.tsx's own "All
 * Documents" view already uses for the identical reason. */
function useCategoryDocs(cat: HubCategoryDef, registerId: string | undefined) {
  const allDocs = useQuery<HubDoc[]>({
    queryKey: ['hubDocsAll'],
    queryFn: documentApi.listAll,
    enabled: !!cat.dpiaOnly,
    staleTime: 0,
  });
  const registerDocs = useQuery<HubDoc[]>({
    queryKey: ['hubDocs', registerId],
    queryFn: () => documentApi.listByRegister(registerId!),
    enabled: !cat.dpiaOnly && !!registerId,
    staleTime: 0,
  });
  if (cat.dpiaOnly) {
    return { ...allDocs, data: (allDocs.data || []).filter((d) => d.documentType === 'DPIA') };
  }
  return registerDocs;
}

export const HubLayout: React.FC = () => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    policies: false, processes: false, 'customer-assurance': false, training: false, dpias: false,
  });
  const [treeOpenMobile, setTreeOpenMobile] = useState(false);

  const { data: registers = [] } = useQuery<any[]>({ queryKey: ['documentRegisters'], queryFn: documentApi.getRegisters });
  const registerIdByCategory = useMemo(() => {
    const map: Record<string, string> = {};
    HUB_CATEGORIES.forEach((c) => {
      if (!c.registerName) return;
      const r = registers.find((reg) => reg.name === c.registerName);
      if (r) map[c.key] = r.registerId;
    });
    return map;
  }, [registers]);

  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const activeCategoryKey = HUB_CATEGORIES.find((c) => location.pathname === `/hub/${c.key}`)?.key;

  return (
    <div className="container-xxl flex-grow-1 container-p-y hub-shell">
      {treeOpenMobile && <div className="hub-tree-overlay" onClick={() => setTreeOpenMobile(false)} />}
      <nav className={`hub-tree ${treeOpenMobile ? 'open' : ''}`}>
        <div className="hub-tree-head">
          <h6 className="mb-0">Content Library</h6>
          <button type="button" className="np-icon-btn d-lg-none" onClick={() => setTreeOpenMobile(false)}>
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="np-search mb-3">
          <i className="ri-search-line"></i>
          <input
            type="text"
            placeholder="Search the Hub…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="hub-tree-legend">
          <span><i className="hub-status-dot np-tone-success"></i>Published</span>
          <span><i className="hub-status-dot np-tone-warning"></i>Review due</span>
          <span><i className="hub-status-dot np-tone-neutral"></i>Draft</span>
        </div>

        {HUB_CATEGORIES.map((cat) => (
          <HubCategoryGroup
            key={cat.key}
            cat={cat}
            registerId={registerIdByCategory[cat.key]}
            search={search}
            open={!!search || !!expanded[cat.key]}
            active={activeCategoryKey === cat.key}
            activeDocId={params.id}
            onToggle={() => setExpanded((e) => ({ ...e, [cat.key]: !e[cat.key] }))}
            onNavigate={() => setTreeOpenMobile(false)}
          />
        ))}
      </nav>

      <div className="hub-main">
        <button type="button" className="np-btn np-btn-ghost np-btn-sm d-lg-none mb-3" onClick={() => setTreeOpenMobile(true)}>
          <i className="ri-menu-line"></i>Browse Hub
        </button>
        <Outlet />
      </div>
    </div>
  );
};

const HubCategoryGroup: React.FC<{
  cat: HubCategoryDef;
  registerId: string | undefined;
  search: string;
  open: boolean;
  active: boolean;
  activeDocId?: string;
  onToggle: () => void;
  onNavigate: () => void;
}> = ({ cat, registerId, search, open, active, activeDocId, onToggle, onNavigate }) => {
  const { data: docs = [] } = useCategoryDocs(cat, registerId);
  const queryClient = useQueryClient();
  const toast = useToast();
  // Ids explicitly clicked open - empty by default means every master with subordinates starts
  // collapsed, with no need to already know which ids have children before data has loaded.
  const [expandedDocs, setExpandedDocs] = useState<Record<string, boolean>>({});
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const q = search.trim().toLowerCase();
  const ordered = useMemo(
    () => [...docs].sort((a, b) => (a.uploadedAt || '').localeCompare(b.uploadedAt || '')),
    [docs],
  );
  const tree = useMemo(() => buildDocTree(ordered), [ordered]);
  // Real depth, not hardcoded to 0 - flattened with nothing collapsed (search should surface a
  // match regardless of whether its ancestor happens to be collapsed right now) so a matched
  // subordinate document still visibly nests under its own policy instead of looking identical
  // to an unrelated top-level one.
  const flatMatches = useMemo(
    () => (q ? flattenDocTree(tree, () => true).filter(({ doc }) => (doc.title || '').toLowerCase().includes(q)) : []),
    [tree, q],
  );

  const reparentMutation = useMutation({
    mutationFn: ({ documentId, parentDocumentId }: { documentId: string; parentDocumentId: string | null }) =>
      documentApi.update(documentId, { parentDocumentId: parentDocumentId || '' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: cat.dpiaOnly ? ['hubDocsAll'] : ['hubDocs', registerId] }),
    onError: (e: Error) => toast.error(`Couldn't group that document: ${parseApiError(e).message}`),
  });

  const handleDrop = useCallback((targetParentId: string | null) => {
    setDropTargetId(null);
    const droppedId = draggedId;
    setDraggedId(null);
    if (!droppedId || droppedId === targetParentId) return;
    reparentMutation.mutate({ documentId: droppedId, parentDocumentId: targetParentId });
  }, [draggedId, reparentMutation]);

  if (q && flatMatches.length === 0) return null;

  return (
    <div className="hub-cat-group">
      <div className={`hub-cat-row-wrap ${active ? 'active' : ''}`}>
        <button
          type="button"
          className={`hub-cat-row ${open ? 'open' : ''} ${dropTargetId === 'root' ? 'hub-drop-target' : ''}`}
          onClick={onToggle}
          onDragOver={(e) => { if (draggedId) { e.preventDefault(); setDropTargetId('root'); } }}
          onDragLeave={() => setDropTargetId((id) => (id === 'root' ? null : id))}
          onDrop={(e) => { e.preventDefault(); handleDrop(null); }}
          title={draggedId ? 'Drop here to remove from its parent policy' : undefined}
        >
          <i className="ri-arrow-right-s-line hub-chev"></i>
          <i className={cat.icon}></i>
          <span className="flex-grow-1 text-start">{cat.shortLabel}</span>
          <span className="hub-cat-count">{docs.length}</span>
        </button>
        <Link to={`/hub/${cat.key}`} className="hub-cat-link" onClick={onNavigate} title={`Open ${cat.shortLabel}`}>
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
      {open && (
        <div className="hub-doc-list">
          {q ? (
            flatMatches.map(({ doc, depth }) => (
              <HubDocLink
                key={doc.documentId}
                doc={doc}
                depth={depth}
                active={activeDocId === doc.documentId}
                onNavigate={onNavigate}
              />
            ))
          ) : (
            tree.map((node) => (
              <HubDocRow
                key={node.doc.documentId}
                node={node}
                depth={0}
                activeDocId={activeDocId}
                onNavigate={onNavigate}
                expandedDocs={expandedDocs}
                onToggleExpand={(id) => setExpandedDocs((prev) => ({ ...prev, [id]: !prev[id] }))}
                draggedId={draggedId}
                dropTargetId={dropTargetId}
                onDragStart={setDraggedId}
                onDragEnd={() => { setDraggedId(null); setDropTargetId(null); }}
                onDragOverRow={setDropTargetId}
                onDrop={handleDrop}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

/** A single row during an active search - flat, no drag/drop or nesting, exactly what the tree
 * always did before real hierarchy existed (a text search doesn't care about position). */
const HubDocLink: React.FC<{
  doc: HubDoc;
  depth: number;
  active: boolean;
  onNavigate: () => void;
}> = ({ doc, depth, active, onNavigate }) => {
  const status = effectiveStatus(doc);
  return (
    <Link
      to={`/hub/documents/${encodeURIComponent(doc.documentId)}`}
      className={`hub-doc-row ${active ? 'active' : ''}`}
      style={{ paddingLeft: `${0.75 + depth * 0.9}rem` }}
      title={doc.title}
      onClick={onNavigate}
    >
      <i className={`hub-status-dot np-tone-${status === 'published' ? 'success' : status === 'stale' ? 'warning' : 'neutral'}`}></i>
      <span className="hub-doc-title">{doc.title || 'Untitled'}</span>
    </Link>
  );
};

/** One node of the real tree, rendered recursively - itself, then (unless collapsed) every one
 * of its own children at depth+1. Draggable onto any other row to become that row's subordinate,
 * or onto the category header (handled by the parent) to become top-level again. */
const HubDocRow: React.FC<{
  node: DocTreeNode<HubDoc>;
  depth: number;
  activeDocId?: string;
  onNavigate: () => void;
  expandedDocs: Record<string, boolean>;
  onToggleExpand: (documentId: string) => void;
  draggedId: string | null;
  dropTargetId: string | null;
  onDragStart: (documentId: string) => void;
  onDragEnd: () => void;
  onDragOverRow: (documentId: string) => void;
  onDrop: (targetParentId: string | null) => void;
}> = ({
  node, depth, activeDocId, onNavigate, expandedDocs, onToggleExpand,
  draggedId, dropTargetId, onDragStart, onDragEnd, onDragOverRow, onDrop,
}) => {
  const { doc, children } = node;
  const status = effectiveStatus(doc);
  const hasChildren = children.length > 0;
  const isCollapsed = !expandedDocs[doc.documentId];
  const isDropTarget = dropTargetId === doc.documentId;
  const isBeingDragged = draggedId === doc.documentId;

  return (
    <>
      <div
        className={`hub-doc-row-wrap ${isDropTarget ? 'hub-drop-target' : ''} ${isBeingDragged ? 'hub-dragging' : ''}`}
        draggable
        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart(doc.documentId); }}
        onDragEnd={onDragEnd}
        onDragOver={(e) => { if (draggedId && draggedId !== doc.documentId) { e.preventDefault(); onDragOverRow(doc.documentId); } }}
        onDragLeave={() => onDragOverRow('')}
        onDrop={(e) => { e.preventDefault(); onDrop(doc.documentId); }}
        title={draggedId && draggedId !== doc.documentId ? `Drop to group under "${doc.title}"` : undefined}
      >
        {hasChildren && (
          <button
            type="button"
            className="hub-doc-collapse-btn"
            style={{ left: `${0.75 + depth * 0.9}rem` }}
            onClick={() => onToggleExpand(doc.documentId)}
            title={isCollapsed ? 'Expand subordinate policies' : 'Collapse subordinate policies'}
          >
            <i className={isCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'}></i>
          </button>
        )}
        <Link
          to={`/hub/documents/${encodeURIComponent(doc.documentId)}`}
          className={`hub-doc-row ${activeDocId === doc.documentId ? 'active' : ''}`}
          style={{ paddingLeft: `${0.75 + depth * 0.9 + (hasChildren ? 1.125 : 0)}rem` }}
          title={doc.title}
          onClick={onNavigate}
          draggable={false}
        >
          <i className={`hub-status-dot np-tone-${status === 'published' ? 'success' : status === 'stale' ? 'warning' : 'neutral'}`}></i>
          <span className="hub-doc-title">{doc.title || 'Untitled'}</span>
        </Link>
      </div>
      {hasChildren && !isCollapsed && children.map((child) => (
        <HubDocRow
          key={child.doc.documentId}
          node={child}
          depth={depth + 1}
          activeDocId={activeDocId}
          onNavigate={onNavigate}
          expandedDocs={expandedDocs}
          onToggleExpand={onToggleExpand}
          draggedId={draggedId}
          dropTargetId={dropTargetId}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOverRow={onDragOverRow}
          onDrop={onDrop}
        />
      ))}
    </>
  );
};

export default HubLayout;
